import { memo, useEffect, useState } from 'react';
import Text from '@wconcept/wconcept-ui-renewal/components/atoms/Text';
import Button from '@wconcept/wconcept-ui-renewal/components/atoms/Button';
import { classNameBind } from '@wconcept/wconcept-common/utils/commonUtils';
import * as S from './style';

interface CustomColorType {
  bgColor?: string;
  textColor?: string;
  pointColor?: string;
  emphasisTextColor?: string;
}

type ColorType = 'white' | 'black' | CustomColorType | string;

interface IProps {
  type?: 'default' | 'small' | 'time' | string; // default: 기본, small: 축약형, time: 시간 포함형
  colorType?: ColorType;
  status?: '1' | '2' | '3' | undefined; // 1: 마감 | 2: 진행중 | 3: 진행 예정
  startDate: string;
  endDate?: string;
  value?: string;
  selectedValue?: string;
  serverTime?: string | Date | undefined;
  onClick?: () => void;
}

const DateTabItem = ({
  type = 'default',
  colorType = 'white',
  status,
  startDate,
  endDate,
  value,
  selectedValue,
  serverTime,
  onClick,
}: IProps) => {
  const [$value, setValue] = useState<string>(String(value));
  const [$selectedValue, setSelectedValue] = useState<string | undefined>(
    selectedValue
  );
  const [eventStatus, setEventStatus] = useState<'1' | '2' | '3'>(
    status || '1'
  ); // 1: 마감 | 2: 진행중 | 3: 진행 예정
  const [serverTimeOffset, setServerTimeOffset] = useState<number | null>(null);

  useEffect(() => {
    setSelectedValue(String(selectedValue));
  }, [selectedValue]);

  useEffect(() => {
    setValue(String(value));
  }, [value]);

  useEffect(() => {
    const fetchServerTime = () => {
      if (serverTime) {
        const serverTimeMs = new Date(serverTime).getTime();
        const localTimeMs = new Date().getTime();
        setServerTimeOffset(serverTimeMs - localTimeMs);
      } else {
        setServerTimeOffset(0);
      }
    };
    fetchServerTime();
  }, [serverTime]);

  useEffect(() => {
    if (serverTimeOffset === null) return;
    if (!startDate || !endDate) return;

    const now = new Date(Date.now() + serverTimeOffset);
    const start = new Date(startDate);
    const end = new Date(endDate || '');
    if (now < start) {
      setEventStatus('3');
    } else if (now >= start && now < end) {
      setEventStatus('2');
    } else {
      setEventStatus('1');
    }
  }, [startDate, endDate, serverTimeOffset]);

  const startDateObj = new Date(startDate);
  const month = startDateObj.getMonth() + 1;
  const day = startDateObj.getDate();

  const getDayOfWeek = (date: Date) => {
    const days = ['일', '월', '화', '수', '목', '금', '토'];
    return days[date.getDay()];
  };

  const dayOfWeek = getDayOfWeek(startDateObj);

  const hours = startDateObj.getHours();
  const startHour = hours % 12 === 0 ? '12' : (hours % 12).toString();
  const amPm = hours < 12 ? '오전' : '오후';
  const timeString = `${amPm} ${startHour}시`;

  const isCustomColor = typeof colorType === 'object';
  const customColors = isCustomColor ? colorType : undefined;

  const cName = classNameBind([
    'date-tab-item item',
    $value === $selectedValue ? 'active' : '',
    eventStatus === '2' ? 'is-today' : '',
    `type-${type}`,
    !isCustomColor ? `bg-type-${colorType}` : '',
  ]);
  return (
    <S.DateTabItem
      className={cName}
      $bgColor={customColors?.bgColor}
      $textColor={customColors?.textColor}
      $pointColor={customColors?.pointColor}
      $emphasisTextColor={customColors?.emphasisTextColor}
    >
      <Button onClick={onClick}>
        {type === 'time' ? (
          <>
            <Text className="date-text">
              {month}.{day}({dayOfWeek})
            </Text>
            <Text className="time-text">{timeString}</Text>
          </>
        ) : (
          <>
            <Text className="day-text">
              {eventStatus === '2' && type === 'default' ? '오늘' : dayOfWeek}
            </Text>
            <Text className="date-text">
              {month}.{day}
            </Text>
          </>
        )}
      </Button>
    </S.DateTabItem>
  );
};

export default memo(DateTabItem);
